/**
 * Shared invoice helpers for the Invoices list and detail pages (Task 21.11).
 *
 * NOTHING HERE COMPUTES A DOCUMENT'S MONEY. Line totals, VAT by rate,
 * öresavrundning and the invoice total are all produced by the Decimal engine
 * in `backend/services/invoicing.py` and stored on the document. This module
 * formats those figures and buckets them for the receivables view; it never
 * re-derives one. A total computed twice is a total that can disagree with
 * itself on a legal document.
 *
 * ONE EXCEPTION, added in Task 22.5: `previewLineMoney`. It prices a DRAFT
 * line the server has not seen yet, so the A4 draft editor can show an amount
 * on a line the admin is still typing. It reproduces
 * `services.invoicing.line_money` exactly, its result is always rendered as a
 * visible preview, and it is replaced by the server's figures on save. It is
 * never used for a document TOTAL — öresavrundning is a server rule and is
 * never guessed at. Read its own docstring before touching it.
 */
import i18n from '../../i18n';
import { getLocale } from '../../lib/format-utils';

/**
 * The states an invoice can be PRESENTED in.
 *
 * These are deliberately NOT the stored `status` values. The stored
 * `status === "sent"` means FINALISED AND LOCKED, not delivered: `sent_at` is
 * written only when a send is confirmed (PRD 10.11, Task 21.12). An invoice
 * that is issued but not emailed is a normal, common state — the admin
 * downloaded the PDF and is sending it themselves — and labelling it "Sent"
 * would be a lie the accountant then acts on.
 */
export const INVOICE_STATE = {
    DRAFT: 'draft',
    ISSUED: 'issued',       // finalised and locked, but nobody has emailed it
    SENT: 'sent',           // sent_at is set — a send actually succeeded
    PARTLY_PAID: 'partly_paid',
    PAID: 'paid',
    OVERDUE: 'overdue',
    CREDITED: 'credited',
};

/** Today as YYYY-MM-DD in the VIEWER's timezone.
 *
 * Local, not UTC: an invoice due "today" in Stockholm must not read as overdue
 * because the browser is an hour ahead of UTC midnight.
 */
export function todayIso() {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
}

/** True when the document is still money somebody owes us.
 *
 * A draft is not a receivable (it is not a document yet), a paid one is
 * settled, and a credited one has been reversed by a credit note.
 *
 * A CREDIT NOTE ITSELF IS NEVER OUTSTANDING (finding 5.200), regardless of
 * status. `services.invoicing.amount_due` gives an issued credit note that
 * names the invoice it corrects a balance of exactly 0.00 — the pair is
 * settled by existing, so it is not collectable and not "money owed back"
 * either. A STANDALONE credit note (no `credit_of_invoice_id` — currently
 * only reachable through the generic create-invoice API, not this UI) keeps
 * the ordinary formula and so carries a NEGATIVE balance: money genuinely
 * owed back to the customer, but the opposite of a receivable, not one at
 * a discount. Either way it does not belong in a view whose whole premise is
 * "what is still owed to us" — a `sent` credit note landing in `buildAging`
 * would inflate the bucket's invoice COUNT in the tied case, and additionally
 * understate the bucket's money TOTAL (via a negative `amount_due` partly
 * cancelling genuine receivables) in the standalone case. This is the only
 * caller of this predicate, so the exclusion lives here rather than at each
 * call site.
 */
export function isOutstanding(invoice) {
    const status = invoice?.status;
    if ((invoice?.doc_type || 'invoice') === 'credit_note') return false;
    return status !== 'draft' && status !== 'paid' && status !== 'credited';
}

/**
 * Whole days from `dueDate` to `today`, or null when there is no due date.
 * Positive means overdue. Compared as UTC midnights so a DST boundary in the
 * viewer's timezone cannot shift the count by a day.
 */
export function daysPastDue(dueDate, today = todayIso()) {
    if (!dueDate || typeof dueDate !== 'string') return null;
    const due = Date.parse(`${dueDate}T00:00:00Z`);
    const now = Date.parse(`${today}T00:00:00Z`);
    if (Number.isNaN(due) || Number.isNaN(now)) return null;
    return Math.round((now - due) / 86400000);
}

/**
 * How this invoice should be shown.
 *
 * OVERDUE RULE: an invoice that is outstanding (see `isOutstanding`) and whose
 * `due_date` is strictly before today is overdue. An invoice due today is not
 * yet overdue. An outstanding invoice with no due date at all is never called
 * overdue — there is nothing to be late against.
 *
 * The backend also has a stored `overdue` status; it is honoured, so a
 * server-side sweep and this derivation can never disagree in the direction
 * that under-reports.
 */
export function deriveInvoiceState(invoice, today = todayIso()) {
    if (!invoice) return INVOICE_STATE.DRAFT;
    const status = invoice.status;

    if (status === 'draft') return INVOICE_STATE.DRAFT;
    if (status === 'credited') return INVOICE_STATE.CREDITED;
    if (status === 'paid') return INVOICE_STATE.PAID;

    const overdue =
        status === 'overdue' || (daysPastDue(invoice.due_date, today) ?? 0) > 0;
    if (overdue) return INVOICE_STATE.OVERDUE;

    if (status === 'partly_paid') return INVOICE_STATE.PARTLY_PAID;

    // status === 'sent' — finalised. Only `sent_at` proves it was emailed.
    return invoice.sent_at ? INVOICE_STATE.SENT : INVOICE_STATE.ISSUED;
}

/** The i18n key for a presented state. */
export function invoiceStateLabelKey(state) {
    return `invoices.status_${state}`;
}

/** Badge variant per state — outline for calm states, destructive for overdue. */
export function invoiceStateVariant(state) {
    if (state === INVOICE_STATE.OVERDUE) return 'destructive';
    if (state === INVOICE_STATE.PAID) return 'default';
    if (state === INVOICE_STATE.DRAFT) return 'secondary';
    return 'outline';
}

/**
 * Format a monetary figure in the invoice's own currency.
 *
 * `formatSEK` in lib/format-utils hardcodes SEK, which would print a EUR
 * invoice as kronor. Two decimals always: this is money on a legal document,
 * not a dashboard rounding.
 *
 * An unrecognised currency code makes Intl throw, so it falls back to a plain
 * number with the code appended rather than blanking the amount.
 */
export function formatMoney(amount, currency = 'SEK') {
    if (amount == null || Number.isNaN(Number(amount))) return '';
    const code = (currency || 'SEK').toUpperCase();
    try {
        return new Intl.NumberFormat(getLocale(), {
            style: 'currency',
            currency: code,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    } catch {
        const plain = new Intl.NumberFormat(getLocale(), {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
        return `${plain} ${code}`;
    }
}

/**
 * The suffix the RENDERER prints after a money figure in this currency.
 *
 * Mirrors `_CURRENCY_SUFFIXES` / `_currency_suffix` in
 * `services/invoice_pdf.py`: "kr" is the Swedish krona's own symbol, not a
 * generic word for money, so every other currency gets its ISO code.
 *
 * This is NOT a formatter — `formatMoney` remains the way amounts are printed.
 * It exists for the one place a currency name is a FACTUAL CLAIM rather than
 * decoration: the `Moms kr` / `Moms EUR` column header, which asserts what the
 * figures beneath it are denominated in.
 */
export function currencySuffix(currency) {
    const code = String(currency || '').trim().toUpperCase();
    if (!code) return '';
    return code === 'SEK' ? 'kr' : code;
}

/**
 * VAT treatments that force every line to 0 % (PRD 10.9).
 *
 * Mirrors `_ZERO_RATED_TREATMENTS` in `services/invoicing.py`. The backend
 * OVERWRITES each line's rate on save under these treatments, so a preview
 * that used the typed rate would show 25 % VAT on a line that will store 0 %.
 */
const ZERO_RATED_TREATMENTS = ['eu_reverse_charge', 'export_outside_eu'];

/** Round to öre with ties away from zero — Python's `ROUND_HALF_UP`.
 *
 * `Math.round` breaks ties toward +∞, which rounds -2.505 to -2.50 where the
 * engine produces -2.51; credit-note lines are negative, so the difference is
 * reachable. The magnitude is rounded and the sign reapplied.
 *
 * `toPrecision(12)` first: 7.4 * 125 is 925.0000000000001 in binary floating
 * point, and a value sitting a hair above a .005 boundary would otherwise
 * round the wrong way. Twelve significant digits is far more than an invoice
 * line carries and far fewer than the artefact.
 */
function roundOre(value) {
    if (!Number.isFinite(value)) return null;
    const scaled = Number((value * 100).toPrecision(12));
    if (!Number.isFinite(scaled)) return null;
    const rounded = scaled < 0 ? -Math.round(-scaled) : Math.round(scaled);
    return rounded / 100;
}

/**
 * A PREVIEW of one line's money, for a draft line the server has not priced.
 *
 * >>> THIS IS THE ONLY MONEY THIS FRONTEND COMPUTES, AND IT IS NEVER STORED,
 * >>> NEVER SUMMED AND NEVER SHOWN AS IF IT WERE FINAL. <<<
 *
 * It exists for one situation: an admin typing a brand-new line on the A4
 * draft surface has no server figure for it at all, and an empty `Belopp`
 * column where an amount belongs is a gap nobody notices. The caller renders
 * the result plainly marked as a preview and replaces it with the server's
 * figures the moment the draft is saved.
 *
 * It reproduces `services.invoicing.line_money` EXACTLY, in its order:
 *
 *     net   = round(quantity × price)          <- rounded PER LINE, first
 *     vat   = round(net × rate / 100)          <- off the ROUNDED net
 *     total = round(net + vat)
 *
 * The order is the whole point. Rounding at the end instead would produce a
 * figure that disagrees with the PDF by an öre on a legal document, which is
 * exactly the class of defect the draft/issued split exists to prevent.
 *
 * Returns null — meaning "show nothing" — for any input the engine itself
 * would refuse, rather than inventing a zero.
 */
export function previewLineMoney(line, vatTreatment) {
    // Blank is not zero. `Number('')` is `0`, which passes `Number.isFinite`
    // below just as happily as a typed "0" would — so a quantity or à pris
    // the admin has not typed anything into yet would otherwise preview a
    // confident "0,00" instead of the "show nothing" this function's own
    // contract promises (finding 5.169). Missing/blank is converted to NaN
    // BEFORE the numeric parse, so it falls through the same finite check a
    // genuinely invalid value does; a real typed zero ("0") still parses to
    // 0 and previews normally.
    const toNumberOrNaN = (value) => {
        const trimmed = String(value ?? '').trim();
        if (trimmed === '') return NaN;
        return Number(trimmed.replace(/\s/g, '').replace(',', '.'));
    };
    const quantity = toNumberOrNaN(line?.quantity);
    const price = toNumberOrNaN(line?.price_per_unit);
    const typedRate = toNumberOrNaN(line?.vat_rate);
    if (!Number.isFinite(quantity) || !Number.isFinite(price) || !Number.isFinite(typedRate)) {
        return null;
    }
    if (typedRate < 0 || typedRate > 100) return null;

    // `_line_vat_rate` quantises the rate before using it, and a zero-rated
    // treatment replaces it outright.
    const rate = ZERO_RATED_TREATMENTS.includes(vatTreatment) ? 0 : roundOre(typedRate);
    if (rate === null) return null;

    const net = roundOre(quantity * price);
    if (net === null) return null;
    const vat = roundOre((net * rate) / 100);
    if (vat === null) return null;
    const total = roundOre(net + vat);
    if (total === null) return null;

    return { net, vat, total };
}

/** Format a quantity — up to 2 decimals, but no trailing ",00" on whole units. */
export function formatQuantity(value) {
    if (value == null || Number.isNaN(Number(value))) return '';
    return new Intl.NumberFormat(getLocale(), {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(value);
}

/**
 * Format a quantity of HOURS for display.
 *
 * Hours are summed client-side in a few places (the billing report groups
 * per-project totals into per-customer ones), and a column of 7.4s accumulated
 * in binary floating point produces 41.400000000000006. Printing that at a
 * customer is not a rounding question, it is a credibility one.
 *
 * So the rounding happens ONCE, here, at the display boundary — the stored and
 * summed values themselves are left alone. A missing or unparseable total
 * prints as zero, which is what the callers showed before.
 *
 * HOURS ONLY. Money is computed and rounded on the server (see the module
 * docstring) and must never be put through a client-side rounding step.
 */
export function formatHours(value) {
    const hours = Number(value);
    return formatQuantity(Number.isFinite(hours) ? hours : 0);
}

/** Format a VAT rate as a percentage, e.g. "25 %" / "12.5 %". */
export function formatPercent(value) {
    if (value == null || Number.isNaN(Number(value))) return '';
    const number = new Intl.NumberFormat(getLocale(), {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(value);
    return `${number} %`;
}

/**
 * Localise a line unit.
 *
 * `unit` is a stable KEY (models/invoice.py KNOWN_UNITS), but an unrecognised
 * value is a legitimate tenant-authored free-text unit and is printed
 * verbatim — exactly what the PDF renderer does, so the screen and the paper
 * agree. The labels themselves mirror `services/invoice_pdf.py`.
 */
export function unitLabel(unit) {
    if (typeof unit !== 'string') return '';
    const key = unit.trim();
    if (!key) return '';
    const translationKey = `invoices.unit_${key}`;
    const translated = i18n.t(translationKey, { defaultValue: '' });
    return translated || key;
}

/**
 * The payment methods offered when registering a payment (PRD 10.12).
 *
 * `method` is a STABLE KEY the frontend localises — the same convention as a
 * line's `unit` — never a display string, so a payment registered in Swedish
 * reads correctly to an English-speaking accountant. The backend types it as
 * free text (`str` ≤ 50) rather than an enum, so a value stored outside this
 * list is legitimate and `paymentMethodLabel` prints it verbatim.
 */
export const PAYMENT_METHODS = [
    'bankgiro',
    'plusgiro',
    'bank_transfer',
    'swish',
    'card',
    'cash',
    'other',
];

/** Localise a payment method key; an unrecognised value is printed as stored. */
export function paymentMethodLabel(method) {
    if (typeof method !== 'string') return '';
    const key = method.trim();
    if (!key) return '';
    return i18n.t(`invoices.payment_method_${key}`, { defaultValue: '' }) || key;
}

/**
 * True when this payment row has been reversed.
 *
 * A voided payment is NEVER removed from `payments[]` and never edited — the
 * mistake and its correction are both facts about the money, and an accountant
 * reconciling against a bank statement next year has to see both (Principle 2).
 * What a void changes is the arithmetic, not the record: `sum_payments` on the
 * backend skips it, so the balance recovers as if it had never been registered.
 *
 * The screen therefore shows a voided row struck through and plainly excluded,
 * rather than hiding it — hiding it would misrepresent a record the backend
 * deliberately preserves.
 */
export function isPaymentVoided(payment) {
    return Boolean(payment?.voided_at);
}

/**
 * True when the backend would ACCEPT a payment on this document.
 *
 * Mirrors `_assert_payable` in routes/invoices.py exactly — issued (a number
 * allocated, not merely out of draft), an invoice rather than a credit note,
 * and not already credited. An action the backend would refuse is not offered
 * at all, the same rule the edit and discard affordances follow.
 */
export function canRegisterPayment(invoice) {
    if (!invoice) return false;
    if (invoice.status === 'draft' || !String(invoice.invoice_number || '').trim()) return false;
    if ((invoice.doc_type || 'invoice') !== 'invoice') return false;
    return invoice.status !== 'credited';
}

/**
 * True when the backend would ACCEPT a credit note against this document.
 *
 * Mirrors `credit_invoice` in routes/invoices.py: issued, an invoice rather
 * than a credit note, and not already credited — `credited_by_invoice_id` is
 * single-valued, so a second credit note is refused.
 */
export function canCreditInvoice(invoice) {
    if (!invoice) return false;
    if (invoice.status === 'draft' || !String(invoice.invoice_number || '').trim()) return false;
    if ((invoice.doc_type || 'invoice') !== 'invoice') return false;
    return invoice.status !== 'credited' && !invoice.credited_by_invoice_id;
}

/** The aging buckets, in display order. */
export const AGING_BUCKETS = ['current', 'd1_30', 'd31_60', 'd60_plus'];

/** Which bucket an outstanding invoice falls into. */
export function agingBucket(invoice, today = todayIso()) {
    const days = daysPastDue(invoice?.due_date, today);
    // No due date means nothing to be late against — it sits under "not due"
    // rather than being invented into an aging band.
    if (days === null || days <= 0) return 'current';
    if (days <= 30) return 'd1_30';
    if (days <= 60) return 'd31_60';
    return 'd60_plus';
}

/**
 * Bucket every outstanding invoice by what is still OWED on it.
 *
 * The amount is `invoice.amount_due` — the SERVER-COMPUTED balance, a
 * `computed_field` on every invoice response (models/invoice.py), derived from
 * the document's live payments by the same Decimal engine that produced its
 * total. Task 21.11 had only the invoiced total to show here because payments
 * did not exist yet; now that they do, showing the invoiced total would tell
 * an admin to chase a customer for money already received.
 *
 * The balance is never re-derived here. Subtracting registered payments in the
 * browser is exactly the client-side money arithmetic that must not happen on
 * a legal figure — and the server's rule is not a subtraction anyway (a
 * credited invoice and a credit note that names its original are both worth
 * 0.00; see `services.invoicing.amount_due`).
 *
 * An OVERPAID invoice carries a negative balance and it is summed as it
 * stands: money owed back to a customer genuinely reduces the receivable, and
 * clamping it at zero would overstate what the company is owed.
 *
 * Totals are kept PER CURRENCY: adding a EUR invoice to a SEK one produces a
 * number that means nothing. Summation runs in integer minor units so a column
 * of öre cannot drift in binary floating point.
 */
export function buildAging(invoices, today = todayIso()) {
    const buckets = {};
    for (const key of AGING_BUCKETS) {
        buckets[key] = { key, count: 0, minorByCurrency: {} };
    }
    const totalMinorByCurrency = {};
    let totalCount = 0;

    for (const invoice of invoices || []) {
        if (!isOutstanding(invoice)) continue;
        const bucket = buckets[agingBucket(invoice, today)];
        const currency = (invoice.currency || 'SEK').toUpperCase();
        const minor = Math.round((Number(invoice.amount_due) || 0) * 100);

        bucket.count += 1;
        bucket.minorByCurrency[currency] = (bucket.minorByCurrency[currency] || 0) + minor;
        totalMinorByCurrency[currency] = (totalMinorByCurrency[currency] || 0) + minor;
        totalCount += 1;
    }

    const toAmounts = (minorByCurrency) =>
        Object.entries(minorByCurrency)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([currency, minor]) => ({ currency, amount: minor / 100 }));

    return {
        totalCount,
        rows: AGING_BUCKETS.map((key) => ({
            key,
            count: buckets[key].count,
            amounts: toAmounts(buckets[key].minorByCurrency),
        })),
        totalAmounts: toAmounts(totalMinorByCurrency),
    };
}

/** Render a per-currency amount list as one string, e.g. "1 000,00 kr · 250,00 €". */
export function formatAmountList(amounts) {
    if (!amounts || amounts.length === 0) return formatMoney(0, 'SEK');
    return amounts.map(({ amount, currency }) => formatMoney(amount, currency)).join(' · ');
}

export function getInvoiceSequence(invoice) {
    if (typeof invoice?.invoice_number_seq === 'number' && !Number.isNaN(invoice.invoice_number_seq)) {
        return invoice.invoice_number_seq;
    }
    const num = Number(invoice?.invoice_number_seq);
    if (!Number.isNaN(num) && num > 0) return num;
    if (invoice?.invoice_number) {
        const match = String(invoice.invoice_number).match(/(\d+)$/);
        if (match) return parseInt(match[1], 10);
    }
    return -1;
}

/**
 * Order invoices newest first.
 *
 * NEVER on `invoice_number`: it is a display STRING carrying the tenant's
 * prefix, so "INV-9" sorts above "INV-10". `invoice_number_seq` is the int,
 * and it is null on every draft — so drafts are ordered by `created_at` and
 * kept at the top, where the work is.
 */
export function sortInvoices(invoices) {
    return [...(invoices || [])].sort((a, b) => {
        const aDraft = a.status === 'draft';
        const bDraft = b.status === 'draft';
        if (aDraft !== bDraft) return aDraft ? -1 : 1;
        if (!aDraft) {
            const aSeq = getInvoiceSequence(a);
            const bSeq = getInvoiceSequence(b);
            if (aSeq !== bSeq) return bSeq - aSeq;
            const bIssued = String(b.issued_at || b.invoice_date || b.created_at || '');
            const aIssued = String(a.issued_at || a.invoice_date || a.created_at || '');
            if (bIssued !== aIssued) return bIssued.localeCompare(aIssued);
        }
        return String(b.created_at || '').localeCompare(String(a.created_at || ''));
    });
}

/**
 * Trigger a browser download for a PDF blob response.
 *
 * The filename comes from the server's Content-Disposition when present — the
 * server names an issued document by its legal invoice number — and falls back
 * to the supplied name only when the header is absent.
 */
export function downloadBlobResponse(response, fallbackName) {
    const disposition = response?.headers?.['content-disposition'] || '';
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    const filename = match ? match[1] : fallbackName;

    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}
